<template>
  <Modal :title="detail.id ? '顧客情報項目編集' : '新規顧客情報項目の追加'" :loading="busy" @close="$emit('close')">
    <div>
      <p class="title">項目名</p>
      <TextInput v-model.trim="label" :error="errors.label.error" />
      <Validation v-if="errors.label.max" message="12文字以内で入力してください" />
    </div>
    <div v-if="isSelect">
      <div class="title">
        選択肢
        <p v-if="detail.id" class="edit-caution">
          選択肢を削除すると既に登録済みの項目内容が顧客データから削除されます
        </p>
      </div>
      <div class="list">
        <div v-for="({ name }, i) in items" :key="i" class="item">
          <TextInput :value="name" @input="updateItemName(i, $event)" :error="listValidate(name)" />
          <div class="buttons">
            <Button small fit :disabled="listCount === 2" @click="deleteItem(i)">
              <DeleteIcon />
            </Button>
            <Button small fit :class="{ hidden: i !== listCount - 1 }" :disabled="listCount === 10" @click="addItem">
              <AddStrongIcon />
            </Button>
          </div>
        </div>
        <div v-if="isSelect" class="validation-list">
          <Validation v-if="errors.list" :message="errors.list" />
        </div>
      </div>
    </div>
    <template #footer-center>
      <Button primary @click="save" :disabled="isDisabled">{{ detail.id ? '保存' : '追加' }}</Button>
    </template>
  </Modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { models } from '@/utils/vue'
import Modal from '@/components/molecules/Modal'
import Button from '@/components/atoms/Button'
import Validation from '@/components/atoms/Validation'
import TextInput from '@/components/atoms/TextInput'
import AddStrongIcon from '@/components/icons/addStrong'
import DeleteIcon from '@/components/icons/delete'

export default {
  name: 'CustomerOptionModal',
  components: {
    Modal,
    Button,
    Validation,
    TextInput,
    AddStrongIcon,
    DeleteIcon,
  },
  props: {
    id: Number,
    inputType: Number,
  },
  computed: {
    ...mapState({
      detail: state => state.customers.options.edit.option,
      busy: state => state.customers.options.edit.busy,
    }),
    ...mapGetters({
      changed: 'customers/options/edit/changed',
    }),
    ...models(
      ['label', 'items'],
      ({ detail }, key) => detail[key],
      ({ $store }, key, value) => $store.commit('customers/options/edit/update', { [key]: value }),
    ),
    isSelect() {
      return this.detail.inputType === 5
    },
    listCount() {
      return this.items.length
    },
    isDisabled() {
      return !this.label || this.errors.label.error || this.hasListMaxError || this.hasNameMultiEmpty || !this.changed
    },
    hasListMaxError() {
      return this.isSelect && this.items.some(({ name }) => this.hasMaxError(name, 8))
    },
    hasNameMultiEmpty() {
      return this.isSelect && this.items.filter(({ name }) => name).length < 2
    },
    errors() {
      const labelError = this.label === null ? null : this.hasMaxError(this.label, 12) || this.label === ''
      const listErrorText = []
      if (this.hasListMaxError) listErrorText.push('8文字以内で入力してください')
      if (this.hasNameMultiEmpty) listErrorText.push('選択肢は2つ以上登録してください')

      return {
        label: { max: this.hasMaxError(this.label, 12), error: labelError },
        list: [...listErrorText],
      }
    },
  },
  mounted() {
    this.$store.dispatch('customers/options/edit/start', { id: this.id, inputType: this.inputType })
  },
  methods: {
    listValidate(value) {
      return this.hasMaxError(value, 8)
    },
    hasMaxError(value, max) {
      return value ? String(value).length > max : null
    },
    async addItem() {
      this.$store.commit('customers/options/edit/addItem')

      await this.$nextTick()
      document.querySelectorAll('.item input')[this.listCount - 1].focus()
    },
    deleteItem(index) {
      this.$store.commit('customers/options/edit/deleteItem', index)
    },
    updateItemName(index, value) {
      this.$store.commit('customers/options/edit/updateItemName', { index, name: value.trim() })
    },
    async save() {
      if (await this.$store.dispatch('customers/options/edit/save')) {
        this.$emit('saved')
        this.$emit('close')
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.title {
  font-weight: bold;
  margin-bottom: 12px;
}

.edit-caution {
  color: var(--pure-red);
  font-size: 12px;
  font-weight: normal;
}

.list {
  display: grid;
  row-gap: 8px;
}

.item {
  column-gap: 16px;
  display: grid;
  grid-template-columns: 1fr auto;
}

.buttons {
  column-gap: 8px;
  display: grid;
  grid-template-columns: auto auto;

  button {
    fill: var(--brand-color);
  }
}

.hidden {
  visibility: hidden;
}

.validation {
  margin-top: 8px;
}

.validation-list .validation {
  margin-top: 0;
}
</style>
